/**
 * The Savings module's wire shapes.
 *
 * Hand-written against `openapi/microcredit-api.v1.json`, and checked back against it by
 * `npm run check:api-drift`. The rule that gate enforces is the one that has cost this codebase
 * real bugs: a field the API may send as `null`, or may not send at all, is nullable here. A
 * property present in the schema's `required` array with no `"null"` in its type union is the only
 * kind that gets to be plain.
 *
 * Two conventions run through the whole file.
 *
 * **Money is an integer count of pesewas**, named `…Minor`, always paired with a `currencyCode`.
 * Nothing here is a decimal cedi figure and nothing is formatted by hand — see
 * `src/components/money-display.tsx`. GHS is the only currency that transacts today; the code
 * travels alongside the amount anyway.
 *
 * **Identifiers are strings.** The spec types several integral fields as `["integer", "string"]`
 * because the server's source-generated serialiser will emit either, so `savings-api.ts`
 * normalises every one of them at the client boundary — the same thing `reference-api.ts` does for
 * branch ids. By the time a value reaches a screen with one of these types it is already a
 * `number` (for a count or an amount) or a `string` (for an identifier).
 */

/**
 * What a product is for, and — because ADR-007 makes them one decision rather than two that can
 * disagree — whether money can be taken back out of it. A security deposit is cash collateral held
 * under a group guarantee; a withdrawable one would be collateral the customer could simply
 * reclaim, so the server refuses that combination outright.
 */
export type SavingsProductClass = 'RegularSavings' | 'SecurityDeposit'

export type SavingsAccountStatus = 'Active' | 'Dormant' | 'Frozen' | 'Closed'

export type InterestPostingFrequency = 'None' | 'Monthly' | 'Quarterly' | 'Annually'

/**
 * Why a slice of a balance is unavailable. `OffsetPending` is placed by the loans module against a
 * defaulting member's security deposit and is not a teller's to invent; `Lien` and `CourtOrder` are.
 */
export type HoldReason = 'OffsetPending' | 'Lien' | 'CourtOrder'

export type PassbookStatus = 'Active' | 'Lost' | 'Replaced' | 'Retired'

/**
 * Statement line types. `WithdrawalFee` matters to a screen out of proportion to its size: the
 * product's withdrawal fee posts as its own line with its own transaction number, so one keyed
 * withdrawal can produce two rows in the passbook.
 */
export type SavingsTransactionType =
  | 'Deposit'
  | 'Withdrawal'
  | 'WithdrawalFee'
  | 'TransferIn'
  | 'TransferOut'
  | 'SecurityDepositIn'
  | 'InterestPosting'
  | 'GuaranteeOffset'
  | 'Refund'

/**
 * The terms an account is opened under.
 *
 * `interestRatePerAnnum` is a **decimal fraction between 0 and 1**, not a percentage: the server
 * documents `0.075` as 7.5%, validates the field as `InclusiveBetween(0, 1)`, and accrues as
 * `balance * rate / 365 * days`. A screen that renders it must multiply by 100 before it writes a
 * `%`, and a form that collects it must divide. Getting this backwards means either a product
 * refused outright for any real rate, or a 7.5% liability created by someone who typed 0.075
 * meaning 0.075%.
 */
export interface SavingsProduct {
  id: string
  code: string
  name: string
  productClass: SavingsProductClass
  currencyCode: string
  isWithdrawable: boolean
  /** A 0–1 fraction. See the note on this interface. */
  interestRatePerAnnum: number
  interestPostingFrequency: InterestPostingFrequency
  minOpeningBalanceMinor: number
  minBalanceMinor: number
  /** Null when the product charges nothing to withdraw — which is not the same as a fee of zero. */
  withdrawalFeeMinor: number | null
  /** Days of customer inactivity before the account ages into dormancy. */
  dormancyDays: number
  liabilityGlAccountCode: string
  interestExpenseGlAccountCode: string
  feeIncomeGlAccountCode: string
  /** A retired product keeps its existing accounts running and takes no new ones. */
  isActive: boolean
}

/**
 * One earmark against a balance.
 *
 * A hold is keyed by its reason on the server: re-posting a hold with a reason that already has an
 * active hold **amends that hold in place** rather than adding a second one. The amount and the
 * note are overwritten and `placedAtUtc` is left as it was, so this row can change under a reader
 * without a new row appearing. `note` is required by the contract and is the only explanation a
 * reviewer will have years later.
 */
export interface AccountHold {
  id: string
  amountMinor: number
  currencyCode: string
  reason: HoldReason
  note: string
  placedAtUtc: string
  /** Null while the hold is still in force. */
  releasedAtUtc: string | null
  isActive: boolean
}

/**
 * An account, its balances, and its holds.
 *
 * The three balances are not interchangeable. `ledgerBalanceMinor` is what the general ledger
 * carries, `holdAmountMinor` is the part of it that is earmarked, and `availableBalanceMinor` is
 * what a withdrawal may draw on. For a security deposit account the available balance is
 * withdrawable by nobody — `isWithdrawable` is false and the server refuses the posting — so a
 * screen must never present it as spendable.
 */
export interface SavingsAccount {
  id: string
  accountNumber: string
  customerId: string
  customerNumber: string
  customerName: string
  productId: string
  productCode: string
  productClass: SavingsProductClass
  branchId: string
  status: SavingsAccountStatus
  openedDate: string
  /** Null until the account is closed. */
  closedDate: string | null
  ledgerBalanceMinor: number
  holdAmountMinor: number
  availableBalanceMinor: number
  currencyCode: string
  /** Null when no physical book was recorded against the account at opening. */
  passbookNumber: string | null
  /** Null on an account that has never moved — which is what dormancy is measured from. */
  lastTransactionDate: string | null
  /** Accrued but not yet capitalised. It is not part of the ledger balance until an interest run posts it. */
  accruedInterestMinor: number
  isWithdrawable: boolean
  holds: AccountHold[]
}


/**
 * One movement, as the passbook records it.
 *
 * `balanceAfterMinor` is the running balance at that line and is the column a statement's meaning
 * lives in — a row read without it says nothing about where the account stood.
 */
export interface SavingsTransaction {
  id: string
  transactionNumber: string
  type: SavingsTransactionType
  /** The branch day the posting landed on, which is not necessarily the day it was keyed. */
  businessDate: string
  /** The day the money economically moved. Backdating is bounded and legitimate. */
  valueDate: string
  amountMinor: number
  currencyCode: string
  balanceAfterMinor: number
  /** Null when the teller typed no narration. */
  narration: string | null
  postedAtUtc: string
  postedBy: string
  /** Null for a line with no journal behind it yet. */
  journalEntryNumber: string | null
  isReversed: boolean
}

/**
 * What a posting answers with.
 *
 * `wasReplay` is the important member. The server matches a replay on `clientOperationId` **alone**
 * — not on the account, the type, or the amount — and answers with the earlier transaction. So a
 * true here means *no second credit was made*, and the figures below describe the original posting,
 * not the one just keyed. A client that reuses an operation id across two real payments will be
 * told, correctly, that it already posted — and the second lot of cash will never be credited.
 */
export interface SavingsPosting {
  transaction: SavingsTransaction
  balanceAfterMinor: number
  availableBalanceMinor: number
  currencyCode: string
  wasReplay: boolean
}

export interface LoanPassbook {
  id: string
  passbookNumber: string
  customerId: string
  status: PassbookStatus
  issuedDate: string
  /** The book this one replaced, or null for a first issue. */
  replacesPassbookId: string | null
}


/**
 * Stable gate codes, as `LoanPrerequisites.unmetGates` reports them. They are codes rather than a
 * boolean because a teller has to tell the member what to bring.
 */
export const PREREQUISITE_GATES = {
  noSavingsAccount: 'savings.prerequisite.no_savings_account',
  savingsAccountNotActive: 'savings.prerequisite.savings_account_not_active',
  noSecurityDepositAccount: 'savings.prerequisite.no_security_deposit_account',
  securityDepositShort: 'savings.prerequisite.security_deposit_short',
  noPassbook: 'savings.prerequisite.no_passbook',
} as const

/*
 * The loan-prerequisite shapes are declared in `loans-types.ts` and re-exported here rather than
 * declared twice.
 *
 * `GET /savings/customers/{customerId}/loan-prerequisites` is a Savings endpoint — it lives in
 * `Modules/Savings/Features/GetLoanPrerequisites` and is gated on `savings.view` — so its shapes
 * would naturally live in this file. They are also, by their nature, what the loans area reads
 * before it opens an account, and that area declared them first.
 *
 * Two declarations of one payload is the thing `check:api-drift` fails the build over, and it is
 * right to: the second is invisible to the gate, so it can drift against the contract with nothing
 * watching. One definition, imported. The savings screens go on importing them from here, so if
 * they are ever moved into this file where they belong, nothing in this area changes.
 */
export type { LoanPassbookRef, LoanPrerequisites, SavingsAccountRef } from './loans-types'

/**
 * What an interest run posted, for one product at one branch.
 *
 * `journalEntryNumber` is null when the run posted nothing — an idle product, or a run already
 * made for that period — and a screen must read that as "no journal", not as "journal withheld".
 */
export interface InterestRun {
  accountsPosted: number
  totalPostedMinor: number
  currencyCode: string
  asOf: string
  journalEntryNumber: string | null
}

/** What a dormancy sweep aged. It moves no money — it changes a status. */
export interface DormancyRun {
  accountsAged: number
  asOf: string
}

/* ------------------------------------------------------------------ requests */

/**
 * A cash or book movement.
 *
 * `clientOperationId` is the idempotency key, and it must be **one per submission, not one per
 * mounted form**. See {@link SavingsPosting.wasReplay} for what reusing it costs.
 *
 * `valueDate` null means the branch's current business date. A date is accepted inside the
 * institution's backdating window and never in the future.
 */
export interface SavingsMovementRequest {
  amountMinor: number
  currencyCode: string | null
  valueDate: string | null
  narration: string | null
  clientOperationId: string | null
  /**
   * The `CustomerVerification` a teller obtained immediately before this posting — see
   * `POST /customers/{id}/verifications`. Whether it must be supplied is server-side policy per
   * operation, not a fixed rule of this shape: a withdrawal's policy is `Required` as shipped, a
   * deposit's is not, and the gate re-validates whichever id is sent regardless.
   */
  verificationId: string | null
}

export interface SavingsTransferRequest {
  destinationAccountId: string
  amountMinor: number
  currencyCode: string | null
  valueDate: string | null
  narration: string | null
  clientOperationId: string | null
  /** The source customer's verification — the account money is leaving, which policy is about. */
  verificationId: string | null
}

export interface OpenSavingsAccountRequest {
  customerId: string
  productId: string
  branchId: string
  openedDate: string
  /** Optional: an account may be opened before a physical book is registered against it. */
  passbookNumber: string | null
}

export interface PlaceHoldRequest {
  amountMinor: number
  /** Null defers to the account's own currency. */
  currencyCode: string | null
  reason: HoldReason
  note: string
}

export interface ReleaseHoldRequest {
  reason: string
}

export interface ReactivateAccountRequest {
  reason: string
}

export interface CloseSavingsAccountRequest {
  reason: string
  valueDate: string | null
}

/**
 * A new product. Dual-controlled, so the answer is a `DualControlResult` and not the product.
 *
 * `interestRatePerAnnum` is the 0–1 fraction — see {@link SavingsProduct}.
 *
 * The three general-ledger codes are each nullable and each defaults differently, which is not
 * what a symmetrical-looking trio suggests. Only the **liability** code is derived from the product
 * class (regular savings and security deposits sit on separate liabilities because the Bank of
 * Ghana deposit return reports them separately). The interest-expense and fee-income codes fall
 * back to fixed institution-wide accounts, identically for both classes.
 */
export interface CreateSavingsProductRequest {
  code: string
  name: string
  productClass: SavingsProductClass
  isWithdrawable: boolean
  interestRatePerAnnum: number
  interestPostingFrequency: InterestPostingFrequency
  minOpeningBalanceMinor: number
  minBalanceMinor: number
  withdrawalFeeMinor: number | null
  dormancyDays: number
  currencyCode: string | null
  liabilityGlAccountCode: string | null
  interestExpenseGlAccountCode: string | null
  feeIncomeGlAccountCode: string | null
}

export interface IssuePassbookRequest {
  passbookNumber: string
  branchId: string
  issuedDate: string
  /** The book being replaced after a loss, or null for a first issue. */
  replacesPassbookId: string | null
}

export interface PostInterestRequest {
  productId: string
  branchId: string
  /** Null runs to the branch's current business date. A future date is refused. */
  asOf: string | null
}

export interface RunDormancyRequest {
  branchId: string
  asOf: string | null
}
